import { AppConfig } from "../AppConfig";
import { ErrorCode, ErrorDetails } from "../Common/Types";
import { RequestInitWithRetry } from "fetch-retry";
import { AUTHORIZATION_SCHEME, fetchWithRetry, wrapWithErrorHandling } from "./baseApi";
import {
  Api,
  InstallmentsResponseDto,
  HttpResponse,
  ProblemDetails,
  MarketSettingsInfo,
} from "./generatedQuotationApi";

export type PaymentDetailsResponse = {
  id: number;
  source: string;
  pageLogId: number;
  amount: number;
  currency: string;
  efComTxLogId: number;
  priceQuoteId: string;
  opportunityId: string;
  opportunityUuid: string;
  externalId: string;
  status: string;
  providerName: string;
  personContactId: string;
  ipAddress: string;
  insertDate: Date;
  correlationId: string;
  termsVersion: string;
  cdrId?: string;
};

export class QuotationApi {
  private readonly quotationApiClient;

  public constructor() {
    const token: string | null = sessionStorage.getItem(AppConfig.sessionStorageKeys.jwtToken);
    if (token == null) {
      throw new ErrorDetails({
        code: ErrorCode.AuthenticationFailed,
        details: { additionalDetails: "Authentication token is missing. Please log in." },
      });
    }
    this.quotationApiClient = new Api({
      baseUrl: `${AppConfig.api.quotation}`,
      securityWorker: () => {
        return {
          headers: {
            Authorization: `${AUTHORIZATION_SCHEME} ${token}`,
          },
        };
      },
      customFetch: (input: string | URL | globalThis.Request, init?: RequestInit) => {
        const withRetryParams: RequestInitWithRetry<typeof global.fetch> = init ?? {};
        withRetryParams.retries = 3;
        withRetryParams.retryDelay = function (attempt) {
          return Math.pow(2, attempt) * 1000;
        };
        return fetchWithRetry(input, withRetryParams);
      },
    });
  }

  public async getInstallments(quoteUuid: string): Promise<InstallmentsResponseDto> {
    return await wrapWithErrorHandling<
      InstallmentsResponseDto,
      HttpResponse<InstallmentsResponseDto, void | ProblemDetails>
    >(ErrorCode.InstallmentsFetchFailed, () => this.quotationApiClient.installments.getInstalltments(quoteUuid));
  }

  public async getMarketSettings(quoteUuid: string): Promise<MarketSettingsInfo> {
    return await wrapWithErrorHandling<MarketSettingsInfo, HttpResponse<MarketSettingsInfo, void | ProblemDetails>>(
      ErrorCode.InstallmentsFetchFailed,
      () => this.quotationApiClient.marketSettings.marketSettingsGetMarketSettings({ quoteUuid })
    );
  }
}
